import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { PREVIEW_SCREENS, type PreviewScreen } from "../../portal/preview";
import { FRAME_SIZES, PortalFrame, SCREEN_LABELS, type FrameTheme } from "./PortalFrame";

/** A theme to show on the real portal page: a variation, a saved theme, or the live one. */
export interface PreviewSubject extends FrameTheme {
  name: string;
}

/**
 * The guest's pages, rendered by the portal bundle itself in a frame and
 * painted with `subject`. Nothing is published or saved by looking.
 */
export function PreviewDialog({ subject, onClose }: { subject: PreviewSubject | null; onClose: () => void }) {
  const [device, setDevice] = useState<"phone" | "desktop">("phone");
  const [screen, setScreen] = useState<PreviewScreen>("login");
  const size = FRAME_SIZES[device];

  return (
    <Dialog open={subject !== null} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Preview: {subject?.name}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={device} onChange={(_, v: "phone" | "desktop" | null) => v && setDevice(v)}>
            <ToggleButton value="phone">Phone</ToggleButton>
            <ToggleButton value="desktop">Desktop</ToggleButton>
          </ToggleButtonGroup>
          <TextField select size="small" label="Page" value={screen} onChange={(e) => setScreen(e.target.value as PreviewScreen)} sx={{ minWidth: 200 }}>
            {PREVIEW_SCREENS.map((s) => <MenuItem key={s} value={s}>{SCREEN_LABELS[s]}</MenuItem>)}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Your portal's real pages with this theme, on sample data. Where a theme leaves the heading empty, guests see your hotspot's own heading.
          </Typography>
        </Stack>
        <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: { xs: 0, sm: 2 } }}>
          {subject && <PortalFrame theme={subject} screen={screen} width={size.width} height={size.height} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
